import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MoonDisc from "@/components/MoonDisc";
import SabbatBadge from "@/components/SabbatBadge";
import { isFullMoonDay, isNewMoonDay } from "@/lib/moon";
import { CATEGORY_META } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { formatDateRange } from "@/lib/format";
import { MapPin, Star } from "lucide-react";
import EventImage from "@/components/EventImage";

export const EventCard = ({ event, index = 0, distanceKm = null }) => {
  const { lang, t, tCategory, tCountry } = useLang();
  const navigate = useNavigate();
  const meta = CATEGORY_META[event.category] || {};
  const Icon = meta.icon;
  const promoActive = event.promoted && (!event.promoted_until || new Date(event.promoted_until) > new Date());
  const sd = event.start_date ? new Date(event.start_date) : null;
  const isFull = sd && isFullMoonDay(sd);
  const isNew = sd && !isFull && isNewMoonDay(sd);

  useEffect(() => {
    if (promoActive) api.post(`/events/${event.slug}/track/view`).catch(() => {});
  }, [promoActive, event.slug]);

  const go = () => {
    if (promoActive) api.post(`/events/${event.slug}/track/click`).catch(() => {});
    navigate(`/events/${event.slug}`);
  };

  return (
    <article
      data-testid={`event-card-${event.slug}`}
      onClick={go}
      className="group relative bg-[#121418] hover:bg-[#181A20] border border-[#272A34] hover:border-amber-500/50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl cursor-pointer fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="relative h-48 overflow-hidden">
        <EventImage
          src={event.image}
          alt={event.title}
          imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent" />
        {(isFull || isNew) && (
          <span data-testid={`moon-badge-${event.slug}`} title={isFull ? t("moon_full") : t("moon_new")} className="absolute top-3 left-3 bg-black/50 rounded-full p-0.5 backdrop-blur border border-amber-800/40">
            <MoonDisc date={sd} size={24} />
          </span>
        )}
        {promoActive && (
          <span data-testid={`promoted-badge-${event.slug}`} className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono tracking-wider uppercase bg-amber-500 text-stone-950 font-bold shadow">
            <Star className="w-3 h-3" /> Promo
          </span>
        )}
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono tracking-wider uppercase bg-amber-950/70 backdrop-blur text-amber-300 border border-amber-800/40">
            {Icon && <Icon className="w-3.5 h-3.5" />} {tCategory(event.category)}
          </span>
          {event.sabbat && <SabbatBadge sabbat={event.sabbat} testid={`sabbat-badge-${event.slug}`} />}
        </div>
      </div>
      <div className="p-5">
        <div className="text-amber-500/90 font-mono text-xs tracking-[0.15em] uppercase mb-2">
          {formatDateRange(event.start_date, event.end_date, lang)}
        </div>
        <h3 className="font-serif text-lg text-stone-100 leading-snug mb-3 line-clamp-2 group-hover:text-amber-100 transition-colors">
          {event.title}
        </h3>
        <div className="flex items-center gap-1.5 text-stone-400 text-sm">
          <MapPin className="w-4 h-4 text-amber-600/80 shrink-0" />
          <span className="truncate">{event.city}, {tCountry(event.country)}</span>
          {distanceKm != null ? (
            <span data-testid={`event-distance-${event.slug}`} className="ml-auto text-xs font-mono text-amber-400 shrink-0">{distanceKm < 1 ? "<1" : distanceKm} km</span>
          ) : event.distance_from_border_km > 0 && (
            <span className="ml-auto text-xs font-mono text-stone-500 shrink-0">~{event.distance_from_border_km} km</span>
          )}
        </div>
      </div>
    </article>
  );
};

export default EventCard;
